import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { useStripeAccount } from "@/modules/finances/hooks/useStripeAccount"
import { cn } from "@/modules/lib/utils"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { useUser } from "@/modules/users/hooks/useUser"
import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { CurrencyFormattedPrice } from "@modules/currency/components/currencyUtils"
import {
  OnBoardingStripeModal,
  type OnBoardingStripeModalRef,
} from "@modules/programs/components/onBoardingStripeModal"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { useProgramAuthorSubscription } from "@modules/programs/hooks/useProgramSubscription"
import { ProgramType } from "@modules/programs/types/programTypes"
import { LoaderCircle } from "lucide-react"
import { useRef } from "react"

type ProgramPriceCardProps = {
  programId: string
  editionAllowed: boolean
  className?: string
} & React.ComponentProps<"div">

export function ProgramPriceCard({ programId, editionAllowed, className, ...props }: ProgramPriceCardProps) {
  const onBoardingStripeModalRef = useRef<OnBoardingStripeModalRef>(null)
  const { navigateToProgramPriceEdition } = useProgramSidebar()
  const { data: program } = useProgram(programId)
  const { updateStripeAccount } = useStripeAccount()
  const { switchToFree, switchToPaying, isUpdating } = useProgramAuthorSubscription({
    programId,
  })
  const user = useUser()

  const onSwitchToPayingClick = async () => {
    if (user.stripeAccountStatus === StripeAccountStatus.OK) switchToPaying()
    else if (
      user.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
      user.stripeAccountStatus === StripeAccountStatus.ONBOARDING
    )
      await updateStripeAccount()
    else onBoardingStripeModalRef.current?.show()
  }

  return (
    <div className={cn("card relative flex flex-col items-start overflow-hidden", className)} {...props}>
      {program.price?.value ? (
        <div className="z-2 flex w-full flex-col gap-4">
          <div className="flex flex-col gap-1">
            {program.type === ProgramType.VIRTUAL || program.type === ProgramType.PRO ? (
              <>
                <Text fontStyle="TM">{word("program.price.membership")}</Text>
                <div className="flex flex-row items-baseline gap-2">
                  <CurrencyFormattedPrice price={program.price} />
                  <Text fontStyle="LL">{word("program.price.pricePerMonth")}</Text>
                </div>
                {program.trialDuration && program.trialDuration > 0 && (
                  <Text className="z-2" fontStyle="BM">
                    {`${word("program.price.trial.description")} ${program.trialDuration.toString()} ${word(
                      "global.day.label",
                      {
                        count: program.trialDuration,
                      },
                    )}`}
                  </Text>
                )}
              </>
            ) : (
              <>
                <Text fontStyle="TM">{word("program.price.singlePurchase")}</Text>
                <div className="flex flex-row items-baseline gap-2">
                  <CurrencyFormattedPrice price={program.price} />
                  <Text fontStyle="LL">{word("program.price.onlyOnce")}</Text>
                </div>
              </>
            )}
            <Text fontStyle="BM">{word("program.price.payingForAll")}</Text>
          </div>

          {editionAllowed && (
            <div className="flex flex-row justify-between">
              <Button
                variant="primary"
                onClick={() => navigateToProgramPriceEdition(program.id)}
                className="self-start"
              >
                {program.type === ProgramType.VIRTUAL || program.type === ProgramType.PRO
                  ? word("program.price.modifyPrice")
                  : word("program.price.modifySinglePrice")}
              </Button>
              <Button
                isLoading={isUpdating}
                variant="underline"
                onClick={switchToFree}
                className="self-start"
                disabled={isUpdating}
              >
                {isUpdating && <LoaderCircle className="animate-spin" />}
                {word("program.price.switchToFree")}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="z-2 flex w-full flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Text fontStyle="TM">{word("program.price.free")}</Text>
            <Text fontStyle="BM">{word("program.price.freeForAll")}</Text>
          </div>
          {editionAllowed && (
            <Button
              variant="primary"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void onSwitchToPayingClick()
              }}
              className="self-start"
            >
              {word("program.price.switchToPaying")}
            </Button>
          )}
        </div>
      )}

      <HustleupSmallIcon
        className="absolute top-0 right-[-40px] h-full bg-cover bg-no-repeat opacity-50"
        color="var(--surface-background)"
      />

      <OnBoardingStripeModal ref={onBoardingStripeModalRef} />
    </div>
  )
}
